/**
 * Top-level route tree for app.vixpro.ai (the SPA).
 *
 * Public routes live at the root.
 * Authenticated routes mirror the URL paths from vixproai-mcp-nextjs so
 * deep links from notifications and emails keep working across the cutover.
 *
 * Marketing lives on vixpro.ai (vixproai-mcp-nextjs). Until cutover, this
 * SPA serves app.vixpro.ai only.
 *
 * Every page is lazy-loaded so the initial bundle only contains the shell +
 * the page the user landed on.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import RequireAuth from './components/RequireAuth';

// ── Public / auth routes (still using Oakallow clone screens; VixPro auth
//    is deferred until we wire up the Oakallow-style passkey flow) ──────────
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const RecoveryPage = lazy(() => import('./pages/RecoveryPage'));
const OAuthAuthorizePage = lazy(() => import('./pages/OAuthAuthorizePage'));
const SubprocessorsPage = lazy(() => import('./pages/SubprocessorsPage'));
const OAuthDocsPage = lazy(() => import('./pages/OAuthDocsPage'));
const ReviewCodePage = lazy(() => import('./pages/review/ReviewCodePage'));
const MFAVerifyPage = lazy(() => import('./pages/MFAVerifyPage'));

// ── VixPro dashboard pages ────────────────────────────────────────────────
const OverviewPage = lazy(() => import('./pages/dashboard/OverviewPage'));
const ActivityPage = lazy(() => import('./pages/activity/ActivityPage'));
const TaskDetailPage = lazy(() => import('./pages/activity/task/TaskDetailPage'));
const IncidentDetailPage = lazy(() => import('./pages/activity/incident/IncidentDetailPage'));
const ProblemDetailPage = lazy(() => import('./pages/activity/problem/ProblemDetailPage'));
const ChangeRedirectPage = lazy(() => import('./pages/activity/change/ChangeRedirectPage'));
const DeletedActivityPage = lazy(() => import('./pages/activity/deleted/DeletedActivityPage'));

const ApprovalsPage = lazy(() => import('./pages/approvals/ApprovalsPage'));
const ApprovalDetailPage = lazy(() => import('./pages/approvals/ApprovalDetailPage'));

const ChangesPage = lazy(() => import('./pages/changes/ChangesPage'));
const ChangeCalendarPage = lazy(() => import('./pages/changes/calendar/ChangeCalendarPage'));
const ChangeDetailPage = lazy(() => import('./pages/changes/detail/ChangeDetailPage'));
const ChangeApprovePage = lazy(() => import('./pages/changes/approve/ChangeApprovePage'));

const IncidentsPage = lazy(() => import('./pages/incidents/IncidentsPage'));
const IncidentApprovePage = lazy(() => import('./pages/incidents/approve/IncidentApprovePage'));

const ServersPage = lazy(() => import('./pages/servers/ServersPage'));
const AddServerPage = lazy(() => import('./pages/servers/add/AddServerPage'));
const InfrastructurePage = lazy(() => import('./pages/infrastructure/InfrastructurePage'));
const ServerContextPage = lazy(() => import('./pages/infrastructure/context/ServerContextPage'));
const DiscoveryPage = lazy(() => import('./pages/discovery/DiscoveryPage'));
const TopologyPage = lazy(() => import('./pages/topology/TopologyPage'));

const AccountPage = lazy(() => import('./pages/dashboard/account/AccountPage'));
const OrganizationPage = lazy(() => import('./pages/dashboard/organization/OrganizationPage'));
const BillingPage = lazy(() => import('./pages/dashboard/organization/settings/billing/BillingPage'));
const LLMProvidersPage = lazy(() => import('./pages/dashboard/organization/settings/llm-providers/LLMProvidersPage'));
const OrgToolsPage = lazy(() => import('./pages/dashboard/organization/settings/tools/OrgToolsPage'));

const DailySummariesPage = lazy(() => import('./pages/daily-summaries/DailySummariesPage'));
const DailySummaryPage = lazy(() => import('./pages/daily-summary/DailySummaryPage'));
const DailySummarySettingsPage = lazy(() => import('./pages/daily-summary/settings/DailySummarySettingsPage'));
const AICostsPage = lazy(() => import('./pages/ai-costs/AICostsPage'));
const MemoryPage = lazy(() => import('./pages/memory/MemoryPage'));
const ActionsPage = lazy(() => import('./pages/actions/ActionsPage'));
const ToolExecutionsPage = lazy(() => import('./pages/tool-executions/ToolExecutionsPage'));
const ToolRequestsPage = lazy(() => import('./pages/tool-requests/ToolRequestsPage'));
const NewToolRequestPage = lazy(() => import('./pages/tool-requests/new/NewToolRequestPage'));
const RunbooksPage = lazy(() => import('./pages/runbooks/RunbooksPage'));
const NewRunbookPage = lazy(() => import('./pages/runbooks/new/NewRunbookPage'));
const PlatformConnectionsPage = lazy(() => import('./pages/platform-connections/PlatformConnectionsPage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const GettingStartedPage = lazy(() => import('./pages/getting-started/GettingStartedPage'));

const SetupConfigurationPage = lazy(() => import('./pages/setup/configuration/SetupConfigurationPage'));
const SetupServersPage = lazy(() => import('./pages/setup/servers/SetupServersPage'));
const SetupAlertSettingsPage = lazy(() => import('./pages/setup/alert-settings/SetupAlertSettingsPage'));
const SetupLLMProviderPage = lazy(() => import('./pages/setup/llm-provider/SetupLLMProviderPage'));
const SetupAnthropicPage = lazy(() => import('./pages/setup/anthropic/SetupAnthropicPage'));
const SetupDeployAgentPage = lazy(() => import('./pages/setup/deploy-agent/SetupDeployAgentPage'));
const SetupDeployFlyioPage = lazy(() => import('./pages/setup/deploy-flyio/SetupDeployFlyioPage'));
const SetupWebhooksPage = lazy(() => import('./pages/setup/webhooks/SetupWebhooksPage'));
const SetupCreditsPage = lazy(() => import('./pages/setup/credits/SetupCreditsPage'));

// ── Embeddable (no auth gate, no app chrome) ──────────────────────────────
const EmbedChatPage = lazy(() => import('./pages/embed/chat/EmbedChatPage'));
const EmbedLoginPage = lazy(() => import('./pages/embed/login/EmbedLoginPage'));

function PageLoading() {
  return (
    <div style={{ padding: '1.5rem', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/recovery" element={<RecoveryPage />} />
          <Route path="/subprocessors" element={<SubprocessorsPage />} />
          <Route path="/docs/oauth" element={<OAuthDocsPage />} />
          <Route path="/review/:platform" element={<ReviewCodePage />} />

          {/* Embeddable (chromeless) */}
          <Route path="/embed/chat" element={<EmbedChatPage />} />
          <Route path="/embed/login" element={<EmbedLoginPage />} />

          {/* Authenticated routes — page chrome (header, mega-menu) rendered
              inside each page for now. A future VixProShell wrapper can move
              the chrome out, but each page currently owns its own Header. */}
          <Route element={<RequireAuth />}>
            <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
            <Route path="/mfa-verify" element={<MFAVerifyPage />} />

            {/* Dashboard / overview */}
            <Route path="/dashboard" element={<OverviewPage />} />
            <Route path="/dashboard/account" element={<AccountPage />} />
            <Route path="/dashboard/organization" element={<OrganizationPage />} />
            <Route path="/dashboard/organization/settings/billing" element={<BillingPage />} />
            <Route path="/dashboard/organization/settings/llm-providers" element={<LLMProvidersPage />} />
            <Route path="/dashboard/organization/settings/tools" element={<OrgToolsPage />} />
            <Route path="/dashboard/servers" element={<ServersPage />} />

            {/* Activity */}
            <Route path="/activity" element={<ActivityPage />} />
            <Route path="/activity/task/:id" element={<TaskDetailPage />} />
            <Route path="/activity/incident/:id" element={<IncidentDetailPage />} />
            <Route path="/activity/problem/:id" element={<ProblemDetailPage />} />
            <Route path="/activity/change/:id" element={<ChangeRedirectPage />} />
            <Route path="/activity/deleted" element={<DeletedActivityPage />} />

            {/* Approvals */}
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/approvals/:requestId" element={<ApprovalDetailPage />} />

            {/* Changes */}
            <Route path="/changes" element={<ChangesPage />} />
            <Route path="/changes/calendar" element={<ChangeCalendarPage />} />
            <Route path="/changes/:changeId" element={<ChangeDetailPage />} />
            <Route path="/changes/:changeId/approve" element={<ChangeApprovePage />} />

            {/* Incidents */}
            <Route path="/incidents" element={<IncidentsPage />} />
            <Route path="/incidents/:incidentId/approve" element={<IncidentApprovePage />} />

            {/* Infrastructure */}
            <Route path="/servers/add" element={<AddServerPage />} />
            <Route path="/infrastructure" element={<InfrastructurePage />} />
            <Route path="/infrastructure/:serverId/context" element={<ServerContextPage />} />
            <Route path="/discovery" element={<DiscoveryPage />} />
            <Route path="/topology" element={<TopologyPage />} />

            {/* Daily summaries / costs / memory / actions */}
            <Route path="/daily-summaries" element={<DailySummariesPage />} />
            <Route path="/daily-summary" element={<DailySummaryPage />} />
            <Route path="/daily-summary/settings" element={<DailySummarySettingsPage />} />
            <Route path="/ai-costs" element={<AICostsPage />} />
            <Route path="/memory" element={<MemoryPage />} />
            <Route path="/actions" element={<ActionsPage />} />

            {/* Tools / runbooks / connections / support */}
            <Route path="/tool-executions" element={<ToolExecutionsPage />} />
            <Route path="/tool-requests" element={<ToolRequestsPage />} />
            <Route path="/tool-requests/new" element={<NewToolRequestPage />} />
            <Route path="/runbooks" element={<RunbooksPage />} />
            <Route path="/runbooks/new" element={<NewRunbookPage />} />
            <Route path="/platform-connections" element={<PlatformConnectionsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/getting-started" element={<GettingStartedPage />} />

            {/* Setup wizards */}
            <Route path="/setup/configuration" element={<SetupConfigurationPage />} />
            <Route path="/setup/servers" element={<SetupServersPage />} />
            <Route path="/setup/alert-settings" element={<SetupAlertSettingsPage />} />
            <Route path="/setup/llm-provider" element={<SetupLLMProviderPage />} />
            <Route path="/setup/anthropic" element={<SetupAnthropicPage />} />
            <Route path="/setup/deploy-agent" element={<SetupDeployAgentPage />} />
            <Route path="/setup/deploy-flyio" element={<SetupDeployFlyioPage />} />
            <Route path="/setup/webhooks" element={<SetupWebhooksPage />} />
            <Route path="/setup/credits" element={<SetupCreditsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
